import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { Button } from 'reactstrap';

import {
    Tooltip
} from '@mui/material';

import {
    AccountingPackage,
    ConnectionDescriptor,
    EntityDetailResponse,
    PortalSafeDataCollectionLimitResponse,
    PortalTextCommon,
    PortalTextCommonImport,
    PortalTextGeneralMessages,
    PortalTextSyncFinancials,
    SubmissionRequest,
} from '../../Models/Api/strongbox.financialportal';
import { ConnectionRequestDescriptor } from '../../Models/Api/strongbox.models';

import { ApplicationState } from '../../Store';
import {
    DirectLinkingDisabled,
    GetDefaultSubmissionOptions,
    GetFilteredAccountingPackages,
    GetShareableLinkParamsFlowSwitch,
    GetStrongboxUrl
} from '../../Store/AppSettings';
import {
    ConnectionOptions,
    ConnectorType,
    GetConnectionOptions,
    actionCreators as ImportFinancialsActions,
    LinkMode,
} from '../../Store/ImportFinancials';
import { GetPortalText } from '../../Store/PortalAssets';
import { actionCreators as SubmissionActionCreators } from '../../Store/Submission';
import { actionCreators as UIStateActionCreators } from '../../Store/UIState';
import { StringFormat } from '../../Utils/StringUtils';

import { GetDataCollectionLimits } from '../../Store/Tenant';

import { ErrorState } from '../ErrorBanner/ErrorBanner';

import { FetchSubmissionRequest } from '../../Services/SubmissionRequestsService';
import { GetWorkspaceDetails } from '../../Services/WorkspaceService';

import {
    CreateSubmissionForWorkspaceId,
    ExecuteImport,
    OnlineLinkFunc,
    RetrieveConnectionToken,
} from './Common';

import { PortalCreateSubmissionRequestOptions, PortalSubmissionRequestFromSubmissionRequest } from '../../Models/PortalCreateSubmissionRequestOptions';
import { FileUploadDataSourceNameId } from '../../Utils/Constants';

import {
    ConnectAccountingSystem,
    GetFinancialsConnectionDescriptor,
    LoadConnectWindow,
    StrongboxConnectionDescriptor,
    StrongboxConnectionRequest
} from '../../Utils/ConnectStrongbox';

import {
    faRefresh,
} from '@awesome.me/kit-8a0d2fd588/icons/modules/sharp/light';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AccountingPackageNameId, resolveAccountingPackage, resolveAccountingPackageNameId, toAccountingPackageNameId, UniversalCollectorAccountingPackageNameId } from '../../Models/Api/AccountingPackage';

export type LinkButtonType = 'large-list-icon' | 'small-button';

type InjectedReduxState = {
    linkParamsActive: boolean;
    strongboxUrl: string;
    connectionOptions?: ConnectionOptions;
    directLinkingDisabled: boolean;
    defaultPortalLinkOptions: PortalCreateSubmissionRequestOptions;
    commonText?: PortalTextCommon;
    syncText?: PortalTextSyncFinancials;
    generalMessages?: PortalTextGeneralMessages;
    dataCollectionLimits?: PortalSafeDataCollectionLimitResponse;
    accountingPackages: AccountingPackage[];
};

type InjectedActionCreators = typeof ImportFinancialsActions & typeof UIStateActionCreators & typeof SubmissionActionCreators;

/**
 * @param buttonType defaults to 'large-list-icon' if undefined
 * */

export type FinancialsRelinkButtonProps = {
    connection?: ConnectionDescriptor;
    datasourceNameId?: string;
    disabledByProp: boolean;
    onSetErrorState: (errorState: ErrorState) => void;
    onSetImporting: (working: boolean) => void;
    workspaceId: string;
    workspaceDisplayName: string;
    submissionId?: string;
    submissionRequestId?: number;
    buttonType?: LinkButtonType;
    buttonContentOverride?: {
        buttonContent: React.ReactElement;
        description: string;
        buttonStyle?: any;
    }
    linkImmediate?: boolean;
}

type Props = FinancialsRelinkButtonProps & InjectedActionCreators & InjectedReduxState;

const FinancialsRelinkButtonComponent: React.FC<Props> = (props): React.ReactElement => {
    const {
        buttonContentOverride,
        buttonType,
        connection,
        datasourceNameId,
        disabledByProp,
        connectionOptions,
        linkParamsActive,
        onSetErrorState,
        onSetImporting,
        strongboxUrl,
        submissionId,
        workspaceId,
        workspaceDisplayName,
        SetImportCustomerFinancialsActive,
        directLinkingDisabled,
        defaultPortalLinkOptions,
        linkImmediate,
        SetPortalWorking,
        SetPortalIdle,
        submissionRequestId,
        UpdateSubmissionStatus,
        syncText,
        generalMessages,
        dataCollectionLimits,
        SetShowDataCollectionLimitsDetails,
        accountingPackages
    } = props;

    let linkButtonStyle: any = {
        marginRight: '10px',
        display: 'flex',
        justifyContent: 'center',
    };
    if (buttonType === 'small-button') {
        linkButtonStyle = {
            ...linkButtonStyle,
            marginLeft: '10px',
            marginRight: 'unset',
        }
    }

    const [sbxConnectionRequestDescriptor, setSbxConnectionRequestDescriptor] = React.useState<ConnectionRequestDescriptor | undefined>(undefined);
    const [sbxConnectionDescriptor, setSbxConnectionDescriptor] = React.useState<StrongboxConnectionDescriptor | undefined>(undefined);
    const [settingUpLinkUrl, setSettingUpLinkUrl] = React.useState<boolean>(false);
    const [fetchingSubmissionRequest, setFetchingSubmissionRequest] = React.useState<boolean>(false);
    const [immediateSubmissionRequest, setImmediateSubmissionRequest] = React.useState<SubmissionRequest | undefined>(undefined);
    const [immediateWorkspaceDetails, setImmediateWorkspaceDetails] = React.useState<EntityDetailResponse | undefined>(undefined);
    const [fetchingWorkspaceDetails, setFetchingWorkspaceDetails] = React.useState<boolean>(false);
    const [failedConnectionToken, setFailedConnectionToken] = React.useState<boolean>(false);

    React.useEffect(() => {
        if ((!!linkImmediate) && (!!datasourceNameId) && (!settingUpLinkUrl)) {
            setSettingUpLinkUrl(true);
            setupLinkUrl();
        }

        // These are the dependencies I actually want.
        // eslint-disable-next-line react-hooks/exhaustive-deps 
    }, [linkImmediate, datasourceNameId])

    React.useEffect(() => {
        if (
            (!!linkImmediate) &&
            (!!submissionId) &&
            (!fetchingSubmissionRequest) &&
            (!!submissionRequestId) &&
            (!immediateSubmissionRequest)
        ) {
            setFetchingSubmissionRequest(true);
            FetchSubmissionRequest(workspaceId, submissionRequestId)
                .then((request) => {
                    setImmediateSubmissionRequest(request);
                })
                .catch((e) => {
                    // Not necessary to handle here, would likely just confuse the user. 1.  it's really unlikely 
                    // to happen.  2.  User won't be able to do anything about it.  3. We can use default values
                })
                .finally(() => {
                    setFetchingSubmissionRequest(false);
                })
        }

        // These are the dependencies I actually want.
        // eslint-disable-next-line react-hooks/exhaustive-deps 
    }, [linkImmediate, submissionId, submissionRequestId])

    React.useEffect(() => {
        if (
            (!!linkImmediate) &&
            (!fetchingWorkspaceDetails) &&
            (!immediateWorkspaceDetails)
        ) {
            setFetchingWorkspaceDetails(true);
            GetWorkspaceDetails(workspaceId)
                .then((workspace) => {
                    setImmediateWorkspaceDetails(workspace);
                })
                .catch((e) => {
                    // Not necessary to handle here, would likely just confuse the user. 1.  it's really unlikely 
                    // to happen.  2.  User won't be able to do anything about it.  3. We can use default values
                })
                .finally(() => {
                    setFetchingWorkspaceDetails(false);
                })
        }

        // These are the dependencies I actually want.
        // eslint-disable-next-line react-hooks/exhaustive-deps 
    }, [linkImmediate, workspaceId])

    const disabled = React.useMemo(() => {
        return (disabledByProp || ((!!linkImmediate) && (settingUpLinkUrl || fetchingSubmissionRequest || fetchingWorkspaceDetails || failedConnectionToken)));
    }, [disabledByProp, settingUpLinkUrl, linkImmediate, fetchingSubmissionRequest, fetchingWorkspaceDetails, failedConnectionToken])

    const setupLinkUrl = async (): Promise<void> => {
        try {
            setFailedConnectionToken(false);
            if (!datasourceNameId) {
                setFailedConnectionToken(true);
                return;
            }
            const accountingPackage = resolveAccountingPackageNameId(datasourceNameId, accountingPackages);
            if (!accountingPackage) {
                setFailedConnectionToken(true);
                return;
            }

            const options =
                (connectionOptions && connectionOptions.options) ||
                defaultPortalLinkOptions;

            const cxnDescriptor = await RetrieveConnectionToken(
                resolveAccountingPackage(accountingPackage, accountingPackages),
                workspaceId,
                options,
                submissionId!,
                strongboxUrl,
                (errorState) => { setFailedConnectionToken(true) },
                (working) => { },   // We use settingUpLinkUrl more broadly to track working state this would deal with so button will be properly disabled.
                (ci) => { },
                true
            );

            setSbxConnectionDescriptor(cxnDescriptor);

            if (!!cxnDescriptor) {
                const cxnRequestDescriptor = await GetFinancialsConnectionDescriptor(
                    cxnDescriptor,
                    (msg: string, detailedMsg: string): void => {
                        setFailedConnectionToken(true);
                    }
                )

                if (!!cxnRequestDescriptor) {
                    setSbxConnectionRequestDescriptor(cxnRequestDescriptor);
                }
            }
        } finally {
            setSettingUpLinkUrl(false);
        }
    }

    const resetLinkUrl = (): void => {
        setSbxConnectionDescriptor(undefined);
        setSbxConnectionRequestDescriptor(undefined);
        setSettingUpLinkUrl(true);
        setupLinkUrl();
    }

    const onAccountingSystemConnected = async (cxnRequest: StrongboxConnectionRequest, requestDescriptor?: ConnectionRequestDescriptor): Promise<void> => {
        const linkOptions: PortalCreateSubmissionRequestOptions = !!immediateSubmissionRequest ?
            PortalSubmissionRequestFromSubmissionRequest(immediateSubmissionRequest) : defaultPortalLinkOptions;

        // Can't really get here if submissionId isn't defined but double check anyway.
        if (!!submissionId) {
            UpdateSubmissionStatus(
                workspaceId,
                submissionId,
                'Normal',
                toAccountingPackageNameId(cxnRequest.accountingPackage)
            );
        }

        const options: ConnectionOptions =
            connectionOptions || {
                connectorType: ConnectorType.fullIntegration,
                workspaceId,
                workspaceName: workspaceDisplayName,
                linkMode: LinkMode.direct,
                chooseAccountingSystem: false,
                options: linkOptions,
            }

        // Accounting system connected, executing import
        ExecuteImport(
            options,
            cxnRequest,
            requestDescriptor,
            (summary, detailed, logProps) => {
                onSetErrorState && onSetErrorState({
                    summaryMessage: summary,
                    extraInformation: detailed,
                    severity: 'Error',
                });
            },
            (financialRecordId, importRequest) => {
                // TODO:  Remove entry from list of pending requests
            },
            (workspaceId, accountingPackage, options) => { },
            immediateWorkspaceDetails?.engagementCode,
            immediateWorkspaceDetails?.primaryEmail
        );
    }

    const executeImmediateConnect = (pkg: AccountingPackageNameId, workspaceId: string, workspaceDisplayName: string): void => {
        if (!(!!sbxConnectionRequestDescriptor && !!datasourceNameId && !!sbxConnectionDescriptor)) {
            return;
        }

        const cxnWindowHandle = LoadConnectWindow(sbxConnectionRequestDescriptor);

        if (!cxnWindowHandle) {
            onSetErrorState && onSetErrorState({
                summaryMessage: generalMessages?.systemErrorConnectingToAccountingSystem || '',
                severity: 'Error',
            });
            return;
        }

        const workingName = `relink-button-connection-${submissionId}`;
        SetPortalWorking(workingName);

        const cxnRequest: StrongboxConnectionRequest = {
            accountingPackage: resolveAccountingPackage(datasourceNameId, accountingPackages)!,
            delegatedAccessToken: sbxConnectionDescriptor.delegatedAccessToken,
            strongboxUri: strongboxUrl,
            orgId: workspaceId,
            submissionId,
            sourceFlow: 'lender',
        }

        ConnectAccountingSystem(
            cxnRequest,
            sbxConnectionRequestDescriptor,
            cxnWindowHandle,
            (msg, detailedMsg, errorCode) => {
                onSetErrorState && onSetErrorState({
                    summaryMessage: msg,
                    extraInformation: detailedMsg,
                    severity: 'Error',
                });
            },
            onAccountingSystemConnected,
            () => { },
            () => false,
            true
        ).finally(() => {
            SetPortalIdle(workingName);
            resetLinkUrl();
        });
    }

    // Depending on the appsettings switch that defines whether the tenant is able to set linking parameters,
    // either retrieve parameters for linking or just start the link.
    //
    // Most of the time we will retrieve parameters and then invoke the process for linking but if the switch
    // in appsettings ("FeatureEnablement" | "ShareableLinkOptionsFlow") is set false then just go straight to 
    // linking as described above.

    const getSavedLinkParametersOrLink = (pkg: AccountingPackageNameId, workspaceId: string, workspaceDisplayName: string): void => {
        const workspaceName = workspaceDisplayName || workspaceId;

        if ((!!dataCollectionLimits) && (dataCollectionLimits.hasLimit) && (dataCollectionLimits.countRemaining! <= 0)) {
            SetShowDataCollectionLimitsDetails(true);
            return;
        }

        if ((!!linkImmediate) && (!!submissionId)) {
            executeImmediateConnect(pkg, workspaceId, workspaceName);
        } else {
            // linkParamsActive is probably going to be true.  This is a setting that comes from 
            // appsettings and it means the user is able to control the parameters for a shareable or direct
            // link.   It can be set false in which case when we get here we should just go straight to linking
            // with the accounting system.

            if (linkParamsActive) {
                // when importingDirect is set (the 3rd parameter), the user can only set direct mode parameters.
                SetImportCustomerFinancialsActive(
                    true,
                    { workspaceId, workspaceName },
                    {
                        accountingPkg: pkg,
                        connectionId: connection?.id,
                    }
                );
            } else {
                // Initialize options to value stored in lenderLinkProps or appropriate 
                // fixed defaults.
                const options =
                    (connectionOptions && connectionOptions.options) ||
                    defaultPortalLinkOptions;

                // We used to check to ensure that the disableFileUpload flag was not enabled, however,
                // it is conceivable and configuration supports that the tenant could choose to disable
                // the ability to CHANGE the allow additional file upload setting while also setting the
                // default value to true.   
                //
                // So the ability to see and change the setting is untethered from what the setting 
                // actually is.

                CreateSubmissionForWorkspaceId(
                    workspaceId,
                    options,
                    onSetErrorState,
                    props.commonText
                )
                    .then(submissionId => {
                        OnlineLinkFunc(
                            resolveAccountingPackage(pkg, accountingPackages),
                            workspaceId,
                            options,
                            submissionId,
                            strongboxUrl,
                            onSetErrorState,
                            onSetImporting,
                            (ci => { })
                        );
                    })
                    .catch(reason => { })
            }
        }
    }

    const getTitleForButton = (accountingPackage: AccountingPackage | undefined | null): string => {
        const accountingSystem = accountingPackage?.friendlyName;
        if (!accountingSystem) {
            return syncText?.syncFinancialData || '';
        }

        return StringFormat(syncText?.templateSyncFinancialsFrom || '{0}', accountingSystem);
    }

    const tooltipTitle = (disabled: boolean, accountingPackage: AccountingPackage | undefined | null): string => {
        const defaultText = syncText?.syncFinancialData || '';

        return disabled ? '' : !!datasourceNameId ? getTitleForButton(accountingPackage) : defaultText;
    }

    const getLargeListButton = (
        accountingPackage: AccountingPackageNameId,
        keyPrefix: string,
        disabled: boolean,
        tooltip: string
    ): React.ReactElement => {
        const keySuffix = submissionId || workspaceId;

        return (
            <Tooltip title={tooltip}>
                <span>
                    <Button
                        key={`${keyPrefix}-${workspaceId}${keySuffix}`}
                        disabled={disabled}
                        style={linkButtonStyle}
                        onClick={() => {
                            getSavedLinkParametersOrLink(accountingPackage, workspaceId, workspaceDisplayName);
                        }}
                        color="secondary"
                    >
                        <FontAwesomeIcon icon={faRefresh} />
                    </Button>
                </span>
            </Tooltip>
        );
    }

    const getSmallListButton = (
        accountingPackage: AccountingPackageNameId,
        idPrefix: string,
        tooltip: string,
        disabled?: boolean,
    ): React.ReactElement => {
        const id = `${idPrefix}${workspaceId}${keySuffix}`;
        return (
            <Button
                key={id}
                className={`submissions-button-icon small-accounting-button`}
                id={id}
                disabled={disabled}
                style={linkButtonStyle}
                onClick={() => {
                    getSavedLinkParametersOrLink(accountingPackage, workspaceId, workspaceDisplayName);
                }}
                color="secondary"
            >
                <Tooltip title={tooltip}>
                    <span>
                        <FontAwesomeIcon icon={faRefresh} />
                    </span>
                </Tooltip>
            </Button>
        );
    }

    // Now begin deciding what visual element we shoud return but first
    // check to see if direct linking is disabled and return nothing if it is

    if (directLinkingDisabled) {
        return (<></>);
    }

    const keySuffix = submissionId || workspaceId;
    let accountingPkgNameId: AccountingPackageNameId | undefined = undefined;
    let accountingPkg: AccountingPackage | undefined = undefined;

    if (!!datasourceNameId) {
        accountingPkg = resolveAccountingPackage(datasourceNameId, accountingPackages);
        accountingPkgNameId = toAccountingPackageNameId(accountingPkg);

        // Temporary fix for hot fix, this needs to not be enabled for universal collector users.  
        // This will handle things sufficiently.  Longer term fix should be to
        // add a column to the accounting package for supporting relink and then check that against
        // the list of accounting packages. 
        if ((datasourceNameId.toLowerCase() === FileUploadDataSourceNameId.toLowerCase()) || (datasourceNameId.toLowerCase() === UniversalCollectorAccountingPackageNameId.toLowerCase())) {
            return (<></>);
        }
    }

    if ((!!buttonContentOverride) && (!!accountingPkg)) {
        const id = `linkfinancialsbutton${workspaceId}${keySuffix}`;
        return (
            <Button
                className={'simple-icon-button'}
                id={id}
                key={id}
                disabled={disabled}
                style={{
                    ...linkButtonStyle,
                    ...buttonContentOverride.buttonStyle
                }}
                onClick={() => {
                    getSavedLinkParametersOrLink(accountingPkgNameId!, workspaceId, workspaceDisplayName);
                }}
                color="primary"
            >
                <Tooltip title={tooltipTitle(disabled, accountingPkg)}>
                    <span>
                        {buttonContentOverride.buttonContent}
                    </span>
                </Tooltip>
            </Button>
        );
    }

    const resolvedAccountingSystem = resolveAccountingPackage(datasourceNameId, accountingPackages);
    if ((!buttonType) || (buttonType === 'large-list-icon')) {
        if (!!resolvedAccountingSystem) {
            return getLargeListButton(
                toAccountingPackageNameId(resolvedAccountingSystem.nameId),
                resolvedAccountingSystem.nameId,
                disabled,
                tooltipTitle(disabled, resolvedAccountingSystem)
            )
        } else {
            // Unknown accounting system.  
            return (<></>);
        }
    }
    if (buttonType === 'small-button') {
        if (!!resolvedAccountingSystem) {
            return getSmallListButton(
                toAccountingPackageNameId(resolvedAccountingSystem.nameId),
                resolvedAccountingSystem.nameId,
                tooltipTitle(disabled, resolvedAccountingSystem),
                disabled
            )
        } else {
            // Unknown accounting system. 
            return (<></>);
        }
    }
    return (<></>);
}

export const FinancialsRelinkButton = connect<InjectedReduxState, InjectedActionCreators, FinancialsRelinkButtonProps, ApplicationState>(
    (appState: ApplicationState) => {
        const commonText = GetPortalText<PortalTextCommon>(appState, 'commonText');
        const commonImportText = GetPortalText<PortalTextCommonImport>(appState, 'commonImport');

        const result: InjectedReduxState = {
            linkParamsActive: GetShareableLinkParamsFlowSwitch(appState),
            strongboxUrl: GetStrongboxUrl(appState) || '',
            connectionOptions: GetConnectionOptions(appState),
            directLinkingDisabled: DirectLinkingDisabled(appState),
            defaultPortalLinkOptions: GetDefaultSubmissionOptions(appState, 'portal', false, commonImportText),
            syncText: GetPortalText<PortalTextSyncFinancials>(appState, 'syncFinancials'),
            generalMessages: GetPortalText<PortalTextGeneralMessages>(appState, 'generalMessages'),
            commonText,
            dataCollectionLimits: GetDataCollectionLimits(appState),
            accountingPackages: GetFilteredAccountingPackages(appState),
        };

        return result;
    },
    dispatch => bindActionCreators(
        {
            ...ImportFinancialsActions,
            ...UIStateActionCreators,
            ...SubmissionActionCreators,
        },
        dispatch
    )
)(FinancialsRelinkButtonComponent);

